import React, { useMemo } from 'react';
import { Card, Row, Col, Image, Typography, Progress, Space } from 'antd';
import { LinkOutlined, PictureOutlined } from '@ant-design/icons';
import { hostMatchesHighlightDomain } from './globalSearchHighlightDomains';

const { Paragraph, Text, Link: TextLink } = Typography;

function sortByScoreDesc(items) {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const sa = typeof a?.score === 'number' && !Number.isNaN(a.score) ? a.score : -1;
    const sb = typeof b?.score === 'number' && !Number.isNaN(b.score) ? b.score : -1;
    return sb - sa;
  });
}

/** 0..1 или 0..100 → проценты для Progress */
function scoreToPercent(score) {
  if (typeof score !== 'number' || Number.isNaN(score)) return null;
  if (score > 1) return Math.min(100, Math.max(0, score));
  return Math.round(score * 1000) / 10;
}

function scoreStrokeColor(pct) {
  if (pct == null) return '#1677ff';
  if (pct >= 70) return '#52c41a';
  if (pct >= 40) return '#faad14';
  return '#ff7875';
}

function hostnameFromRawUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const s = raw.trim();
  try {
    let href = s;
    if (s.startsWith('//')) href = `https:${s}`;
    else if (!/^https?:\/\//i.test(s)) href = `https://${s}`;
    const u = new URL(href);
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    return host || null;
  } catch {
    return null;
  }
}

/** Подпись домена с учётом watchlist: если совпал только хост картинки — показываем его */
function domainLabelForHit(hit) {
  const pageHost = hostnameFromRawUrl(hit?.pageUrl);
  const imgHost = hostnameFromRawUrl(hit?.img_url);
  const pageMatch = pageHost && hostMatchesHighlightDomain(pageHost);
  const imgMatch = imgHost && hostMatchesHighlightDomain(imgHost);
  if (pageMatch) return pageHost;
  if (imgMatch) return imgHost;
  return pageHost || imgHost || null;
}

/** Совпадение с watchlist по странице или по URL картинки */
function hitMatchesHighlightDomains(hit) {
  const pageHost = hostnameFromRawUrl(hit?.pageUrl);
  const imgHost = hostnameFromRawUrl(hit?.img_url);
  return (
    (pageHost && hostMatchesHighlightDomain(pageHost)) ||
    (imgHost && hostMatchesHighlightDomain(imgHost))
  );
}

function SimilaritiesBlock({ data }) {
  if (data == null) return null;
  if (Array.isArray(data)) {
    return (
      <ul className="global-search-report__legacy-list">
        {data.map((item, i) => (
          <li key={i}>
            {typeof item === 'object' ? (
              <pre className="global-search-report__legacy-pre">{JSON.stringify(item, null, 2)}</pre>
            ) : (
              String(item)
            )}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <pre className="global-search-report__legacy-pre">{JSON.stringify(data, null, 2)}</pre>
  );
}

/**
 * Отчёт: карточки выдачи Yandex с сходством. Старый формат: yandexUrls + similarities.
 */
export function GlobalSearchResultReport({ payload, dense }) {
  const items = useMemo(() => sortByScoreDesc(payload?.yandexSearch), [payload?.yandexSearch]);

  const hasNew =
    Array.isArray(payload?.yandexSearch) && payload.yandexSearch.length > 0;
  const hasLegacyUrls =
    Array.isArray(payload?.yandexUrls) && payload.yandexUrls.length > 0;
  const hasLegacySim = payload?.similarities != null;

  if (!payload) return null;

  return (
    <div className={`global-search-report${dense ? ' global-search-report--dense' : ''}`}>
      {hasNew ? (
        <section className="global-search-report__hits">
          <div className="global-search-report__hits-head">
            <Text type="secondary" className="global-search-report__hits-sub">
              {items.length} {items.length === 1 ? 'результат' : 'результатов'}
              {items.some((x) => typeof x?.score === 'number') ? ' · сортировка по сходству' : ''}
            </Text>
          </div>
          <Row gutter={[16, 16]}>
            {items.map((hit, i) => {
              const highlighted = hitMatchesHighlightDomains(hit);
              const domainLabel = domainLabelForHit(hit);
              const hasScore = typeof hit.score === 'number' && !Number.isNaN(hit.score);
              return (
              <Col xs={24} sm={12} lg={8} key={`yandex-hit-${i}-${hit.pageUrl || ''}-${hit.img_url || ''}`}>
                <Card
                  className={`global-search-report__card${highlighted ? ' global-search-report__card--highlight-domain' : ''}`}
                  hoverable
                  cover={
                    <div className="global-search-report__cover-wrap">
                      {typeof hit.score === 'number' && !Number.isNaN(hit.score) ? (
                        <div className="global-search-report__score-pill" aria-label="Сходство">
                          {scoreToPercent(hit.score).toFixed(1)}%
                        </div>
                      ) : null}
                      {hit.img_url ? (
                        <Image
                          src={hit.img_url}
                          alt=""
                          className="global-search-report__cover-img"
                          preview={{ mask: 'Просмотр' }}
                        />
                      ) : (
                        <div className="global-search-report__cover-placeholder">
                          <PictureOutlined />
                        </div>
                      )}
                    </div>
                  }
                >
                  <div className="global-search-report__card-body">
                    <Paragraph strong className="global-search-report__card-title" ellipsis={{ rows: 2 }}>
                      {hit.title || 'Без названия'}
                    </Paragraph>
                    {hit.description ? (
                      <Paragraph type="secondary" className="global-search-report__card-desc" ellipsis={{ rows: 3 }}>
                        {hit.description}
                      </Paragraph>
                    ) : null}
                    {hasScore ? (
                      <div className="global-search-report__progress-wrap">
                        <Progress
                          percent={scoreToPercent(hit.score)}
                          size="small"
                          showInfo={false}
                          strokeColor={scoreStrokeColor(scoreToPercent(hit.score))}
                        />
                        <Text
                          type="secondary"
                          className={`global-search-report__progress-caption${highlighted ? ' global-search-report__progress-caption--highlight-domain' : ''}`}
                          title={hit.pageUrl || hit.img_url || ''}
                        >
                          {domainLabel || '—'}
                        </Text>
                      </div>
                    ) : highlighted ? (
                      <div className="global-search-report__progress-wrap global-search-report__progress-wrap--domain-only">
                        <Text
                          type="secondary"
                          className="global-search-report__progress-caption global-search-report__progress-caption--highlight-domain"
                          title={hit.pageUrl || hit.img_url || ''}
                        >
                          {domainLabel || '—'}
                        </Text>
                      </div>
                    ) : null}
                    {hit.pageUrl ? (
                      <Space size={4} className="global-search-report__link-row">
                        <LinkOutlined className="global-search-report__link-icon" />
                        <TextLink href={hit.pageUrl} target="_blank" rel="noopener noreferrer">
                          Открыть страницу
                        </TextLink>
                      </Space>
                    ) : null}
                  </div>
                </Card>
              </Col>
            );
            })}
          </Row>
        </section>
      ) : null}

      {hasLegacyUrls ? (
        <section className="global-search-report__legacy">
          <span className="global-search-report__hits-title">Ссылки</span>
          <ul className="global-search-report__legacy-urls">
            {payload.yandexUrls.map((url, i) => (
              <li key={i}>
                <TextLink href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </TextLink>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {hasLegacySim ? (
        <section className="global-search-report__legacy">
          <span className="global-search-report__hits-title">Доп. данные сходства</span>
          <SimilaritiesBlock data={payload.similarities} />
        </section>
      ) : null}
    </div>
  );
}

export default GlobalSearchResultReport;
